import React from 'react';
import PropTypes from 'prop-types';
import Script from 'next/script';
import { CookieCompliance, CookieComplianceCategories, isMobileDevice } from '@deporvillage/utils-library-component';
import { CartContext } from '../../../context/cart/context';

const getTrackingEvent = (page, model) => {
    switch (page) {
        case 'subcategory':
        case 'category':
        case 'landing':
        case 'search':
            return { 
                event: 'viewList', 
                item: model?.items?.slice(0, 3).reduce((p, {sku}) => [...p, sku], []) || [],
            };
        case 'product':
            return { event: 'viewItem', item: model.sku };
        case 'home':
            return { event: 'viewHome' };
        default:
            return { event: 'viewPage' };
    }
};

const trackView = (id, page, model, hashEmail) => {
    // istanbul ignore next
    const deviceType = isMobileDevice() ? 'm' : 'd';
    if (hashEmail) {
        window.criteo_q.push(
            { event: 'setAccount', account: id },
            { event: 'setEmail', email: hashEmail, hash_method: 'sha256' },
            { event: 'setSiteType', type: deviceType },
            getTrackingEvent(page, model),
        );
    } else {
        window.criteo_q.push(   
            { event: 'setAccount', account: id },
            { event: 'setSiteType', type: deviceType },
            getTrackingEvent(page, model),
        );
    }
};

const trackCart = (id, model, hashEmail) => {
    // istanbul ignore next
    const deviceType = isMobileDevice() ? 'm' : 'd';
    // istanbul ignore if
    if (hashEmail) {
        window.criteo_q.push(
            { event: 'setAccount', account: id },
            { event: 'setEmail', email: hashEmail, hash_method: 'sha256' },
            { event: 'setSiteType', type: deviceType },
            { event: 'addToCart', item: model },
        );
    } else {
        window.criteo_q.push(
            { event: 'setAccount', account: id },
            { event: 'setSiteType', type: deviceType },
            { event: 'addToCart', item: model },
        );
    }
};

const mapCartItem = ({ default_variant: variant, quantity, sku }) => ({
    id: sku,
    quantity,
    price: variant?.price?.price,
});

const CriteoOneTag = ({ id, model, page, script }) => {
    const mounted = React.useRef(false);
    const [cartData] = React.useContext(CartContext);
    const [isInit, setInit] = React.useState(false);
    const [hashEmail, setHashEmail] = React.useState(null);

    const handleUserData = (data) => {
        window.removeEventListener('user:data', handleUserData);
        const hashEmail = (data.detail?.logged_in && data.detail?.criteo_hash_email) || null;
        setHashEmail(hashEmail);
        trackView(id, page, model, hashEmail);
    };

    const handleInit = (canRun) => mounted.current && setInit(canRun);

    React.useEffect(() => {
        if (cartData?.isOpened) {
            const model = cartData.items.map(mapCartItem);
            trackCart(id, model, hashEmail);
        }
    }, [cartData]);

    React.useEffect(() => {
        mounted.current = true;
        CookieCompliance(handleInit, CookieComplianceCategories.Targeting);
        window.criteo_q = window.criteo_q || [];
        window.addEventListener('user:data', handleUserData);
        window.dispatchEvent(new CustomEvent('user:getdata'));
        return () => {
            window.removeEventListener('user:data', handleUserData);
            mounted.current = false;
        };
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    return id && isInit && (
        <Script 
            id={'frontend-common-criteo-one-tag'}
            src={script}
            strategy={'lazyOnload'}
        />
    );
};

CriteoOneTag.propTypes = {
    id: PropTypes.number,
    model: PropTypes.object,
    page: PropTypes.string,
    script: PropTypes.string,
};

export default CriteoOneTag;
